import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, Search, X } from "lucide-react";
import { clsx } from "clsx";

interface MultiSelectProps {
  options: { value: string; label: string }[];
  /** Currently selected values. Empty array = nothing selected (i.e. "all"). */
  value: string[];
  onChange: (values: string[]) => void;
  /** Shown in the trigger when nothing is selected. */
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  /** Cap the height of the selected-chips area (px); excess chips scroll instead of
   * growing the control unboundedly. */
  chipsMaxHeight?: number;
}

/**
 * Searchable multiselect with chips. Matches the look of {@link SearchableSelect}
 * but lets the user pick several values at once. Empty selection means "no filter".
 */
export function MultiSelect({
  options,
  value,
  onChange,
  placeholder = "— wszystkie —",
  searchPlaceholder = "Szukaj...",
  disabled,
  className,
  id,
  chipsMaxHeight,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const selected = options.filter((o) => value.includes(o.value));
  const filtered = search.trim()
    ? options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))
    : options;

  const toggle = (v: string) =>
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);

  // "Select all" works on the filtered list: with an active search it adds just the
  // matches (keeping the current selection), without search it selects everything.
  const allFilteredSelected =
    filtered.length > 0 && filtered.every((o) => value.includes(o.value));
  const selectAllFiltered = () =>
    onChange([...new Set([...value, ...filtered.map((o) => o.value)])]);

  const handleTriggerClick = () => {
    if (disabled) return;
    const next = !open;
    setOpen(next);
    if (next) setTimeout(() => searchRef.current?.focus(), 30);
  };

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        id={id}
        onClick={handleTriggerClick}
        disabled={disabled}
        className={clsx(
          "input-base w-full text-left flex items-start gap-2 min-h-[38px]",
          disabled && "opacity-50 cursor-not-allowed",
          className,
        )}
      >
        <span
          className="flex-1 flex flex-wrap items-center gap-1 min-w-0"
          style={chipsMaxHeight ? { maxHeight: chipsMaxHeight, overflowY: "auto" } : undefined}
        >
          {selected.length === 0 ? (
            <span className="text-gray-400 dark:text-gray-500">{placeholder}</span>
          ) : (
            selected.map((o) => (
              <span
                key={o.value}
                className="inline-flex items-center gap-1 max-w-[160px] pl-1.5 pr-1 py-0.5 rounded bg-[var(--color-primary)]/10 text-[var(--color-primary)] text-xs"
              >
                <span className="truncate">{o.label}</span>
                <X
                  size={12}
                  className="shrink-0 cursor-pointer hover:text-[var(--color-primary)]/70"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle(o.value);
                  }}
                />
              </span>
            ))
          )}
        </span>
        <ChevronDown
          size={14}
          className={clsx(
            "shrink-0 mt-0.5 text-gray-400 transition-transform duration-150",
            open && "rotate-180",
          )}
        />
      </button>

      {open && (
        <div className="absolute z-[200] left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-xl">
          <div className="p-2 border-b border-gray-100 dark:border-gray-700/50 flex items-center gap-2">
            <div className="relative flex-1">
              <Search
                size={12}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    setOpen(false);
                    setSearch("");
                  }
                  if (e.key === "Enter" && filtered[0]) {
                    e.preventDefault();
                    toggle(filtered[0].value);
                  }
                }}
                placeholder={searchPlaceholder}
                className="w-full pl-7 pr-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
              />
            </div>
            {!allFilteredSelected && filtered.length > 0 && (
              <button
                type="button"
                onClick={selectAllFiltered}
                className="shrink-0 text-xs font-mono uppercase tracking-wider text-gray-400 hover:text-[var(--color-primary)]"
                title={search.trim() ? "Zaznacz wszystkie wyniki wyszukiwania" : "Zaznacz wszystkie"}
              >
                Wszystkie
              </button>
            )}
            {value.length > 0 && (
              <button
                type="button"
                onClick={() => onChange([])}
                className="shrink-0 text-xs font-mono uppercase tracking-wider text-gray-400 hover:text-[var(--color-primary)]"
              >
                Wyczyść
              </button>
            )}
          </div>

          <ul className="max-h-56 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <li className="px-3 py-3 text-sm text-center text-gray-400">Brak wyników</li>
            ) : (
              filtered.map((opt) => {
                const isSel = value.includes(opt.value);
                return (
                  <li key={opt.value}>
                    <button
                      type="button"
                      onClick={() => toggle(opt.value)}
                      className={clsx(
                        "w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700/50",
                        isSel
                          ? "text-[var(--color-primary)] font-medium"
                          : "text-gray-900 dark:text-gray-100",
                      )}
                    >
                      <span
                        className={clsx(
                          "w-4 h-4 shrink-0 rounded border flex items-center justify-center",
                          isSel
                            ? "bg-[var(--color-primary)] border-[var(--color-primary)] text-white"
                            : "border-gray-300 dark:border-gray-600",
                        )}
                      >
                        {isSel && <Check size={11} strokeWidth={3} />}
                      </span>
                      <span className="flex-1 truncate">{opt.label}</span>
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
